import { useState } from "react";
import { Copy, Link2, Mail, QrCode, RefreshCw, UserMinus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { btnGhost, btnGold, errMsg, inp, lbl } from "./ui";

export default function UsherPanel({ event, ushers, onChange, disabled, onQr }) {
  const [f, setF] = useState({ email: "", name: "", zone_ids: [event.zones[0]?.id].filter(Boolean) });
  const [lastLink, setLastLink] = useState(null);
  const zoneName = Object.fromEntries(event.zones.map((z) => [z.id, z.name]));
  const toggle = (ids, id) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  const base = `/attendance/events/${event.id}/ushers`;

  const invite = async (e) => {
    e.preventDefault();
    if (!f.zone_ids.length) return toast.error("Pick at least one zone");
    try {
      const r = await api.post(base, { ...f, origin_url: window.location.origin });
      setLastLink({ email: f.email, link: r.data.link, sent: r.data.email_sent });
      toast.success(r.data.email_sent ? `Invitation emailed to ${f.email}` : "Usher added — copy their link below");
      setF({ ...f, email: "", name: "" }); onChange();
    } catch (err) { toast.error(errMsg(err, "Invite failed")); }
  };
  const act = async (fn, ok) => { try { const r = await fn(); if (ok) toast.success(ok); onChange(); return r; } catch (err) { toast.error(errMsg(err)); } };
  const regen = async (u) => {
    const r = await act(() => api.post(`${base}/${u.id}/regenerate`, { origin_url: window.location.origin }));
    if (r) setLastLink({ email: u.email, link: r.data.link, sent: r.data.email_sent });
  };
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Link copied"));

  return (
    <div data-testid="att-usher-panel">
      {!disabled && (
        <form onSubmit={invite} className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Usher email *</label><input required type="email" data-testid="att-usher-email" className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><label className={lbl}>Name</label><input data-testid="att-usher-name" className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="sm:col-span-2 flex flex-wrap gap-2">
            {event.zones.map((z) => (
              <button type="button" key={z.id} data-testid={`att-usher-zone-${z.id}`} onClick={() => setF({ ...f, zone_ids: toggle(f.zone_ids, z.id) })}
                className={`px-3 py-1.5 rounded-full text-xs border ${f.zone_ids.includes(z.id) ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 text-neutral-400"}`}>{z.name}</button>
            ))}
          </div>
          <button data-testid="att-usher-invite" className={`${btnGold} sm:col-span-2`}><UserPlus size={14} /> Invite usher</button>
        </form>
      )}
      {lastLink && (
        <div className="mt-4 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-3 text-xs" data-testid="att-usher-last-link">
          <div className="text-neutral-300 mb-2">{lastLink.sent ? <><Mail size={12} className="inline" /> Emailed to {lastLink.email}. </> : "Email not sent — share this personal link:"} It's shown only once.</div>
          <div className="flex gap-2"><input readOnly className={`${inp} text-xs`} value={lastLink.link} data-testid="att-usher-link-value" />
            <button type="button" onClick={() => copy(lastLink.link)} className={btnGhost} data-testid="att-usher-copy"><Copy size={13} /></button></div>
        </div>
      )}
      <div className="mt-6 space-y-2">
        {ushers.map((u) => (
          <UsherRow key={u.id} u={u} event={event} zoneName={zoneName} disabled={disabled}
            onZones={(ids) => act(() => api.put(`${base}/${u.id}`, { zone_ids: ids }), "Zones updated")}
            onRevoke={() => window.confirm(`Revoke ${u.email}'s access?`) && act(() => api.post(`${base}/${u.id}/revoke`), "Access revoked")}
            onRegen={() => regen(u)} onQr={onQr ? () => onQr(u.id) : null} />
        ))}
        {!ushers.length && <p className="text-sm text-neutral-500">No ushers yet.</p>}
      </div>
    </div>
  );
}

function UsherRow({ u, event, zoneName, disabled, onZones, onRevoke, onRegen, onQr }) {
  const revoked = u.status === "revoked";
  return (
    <div data-testid={`att-usher-row-${u.id}`} className={`rounded-lg border border-white/10 p-3 ${revoked ? "opacity-40" : ""}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <div className="text-sm text-white">{u.name || u.email} <span className="text-neutral-500 text-xs">{u.name && u.email}</span></div>
        <span className="text-[9px] tracking-widest uppercase text-neutral-400" data-testid={`att-usher-status-${u.id}`}>{u.status}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {event.zones.map((z) => {
          const on = u.zone_ids.includes(z.id);
          return (
            <button key={z.id} disabled={revoked || disabled} onClick={() => { const ids = on ? u.zone_ids.filter((x) => x !== z.id) : [...u.zone_ids, z.id]; if (ids.length) onZones(ids); }}
              data-testid={`att-usher-${u.id}-zone-${z.id}`}
              className={`px-2.5 py-1 rounded-full text-[11px] border ${on ? "border-[#D4AF37]/70 text-[#D4AF37]" : "border-white/10 text-neutral-600"}`}>{zoneName[z.id]}</button>
          );
        })}
      </div>
      {!revoked && !disabled && (
        <div className="flex gap-2 mt-3">
          <button onClick={onRegen} data-testid={`att-usher-regen-${u.id}`} className="text-[11px] text-neutral-400 hover:text-[#D4AF37] inline-flex items-center gap-1"><RefreshCw size={11} /> New link</button>
          {onQr && <button onClick={onQr} data-testid={`att-usher-qr-${u.id}`} className="text-[11px] text-neutral-400 hover:text-[#D4AF37] inline-flex items-center gap-1 ml-3"><QrCode size={11} /> QR</button>}
          <button onClick={onRevoke} data-testid={`att-usher-revoke-${u.id}`} className="text-[11px] text-neutral-400 hover:text-red-400 inline-flex items-center gap-1 ml-3"><UserMinus size={11} /> Revoke</button>
          {u.last_seen_at && <span className="text-[11px] text-neutral-600 ml-auto inline-flex items-center gap-1"><Link2 size={11} /> seen {new Date(u.last_seen_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>}
        </div>
      )}
    </div>
  );
}
